import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ForgotPasswordForm = ({ currentLanguage }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const translations = {
    EN: {
      title: 'Forgot Password',
      subtitle: 'Enter your email address and we will send you instructions to reset your password',
      emailLabel: 'Email Address',
      emailPlaceholder: 'Enter your email address',
      sendButton: 'Send Reset Link',
      backToLogin: 'Back to Login',
      successTitle: 'Check Your Email',
      successMessage: 'We have sent password reset instructions to your email address. Please check your inbox and follow the instructions.',
      successNote: 'If you do not receive an email within a few minutes, please check your spam folder.',
      invalidEmail: 'Please enter a valid email address',
      systemError: 'Unable to process your request. Please try again later.',
      notFound: 'No account found with this email address',
    },
    FR: {
      title: 'Mot de passe oublié',
      subtitle: 'Entrez votre adresse e-mail et nous vous enverrons des instructions pour réinitialiser votre mot de passe',
      emailLabel: 'Adresse e-mail',
      emailPlaceholder: 'Entrez votre adresse e-mail',
      sendButton: 'Envoyer le lien de réinitialisation',
      backToLogin: 'Retour à la connexion',
      successTitle: 'Vérifiez votre e-mail',
      successMessage: 'Nous avons envoyé des instructions de réinitialisation du mot de passe à votre adresse e-mail. Veuillez vérifier votre boîte de réception et suivre les instructions.',
      successNote: 'Si vous ne recevez pas d\'e-mail dans quelques minutes, veuillez vérifier votre dossier spam.',
      invalidEmail: 'Veuillez entrer une adresse e-mail valide',
      systemError: 'Impossible de traiter votre demande. Veuillez réessayer plus tard.',
      notFound: 'Aucun compte trouvé avec cette adresse e-mail',
    },
  };

  const t = translations?.[currentLanguage];

  const mockValidEmails = [
    'admin@clearmotion.com',
    'sarah.johnson@clearmotion.com',
    'marie.dubois@clearmotion.com',
    'jean.martin@clearmotion.com',
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!email?.trim()) {
      setError(t?.invalidEmail);
      return;
    }

    if (!validateEmail(email)) {
      setError(t?.invalidEmail);
      return;
    }

    if (!mockValidEmails?.includes(email?.toLowerCase())) {
      setError(t?.notFound);
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      setIsSuccess(true);
    }, 1500);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (isSuccess) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-card rounded-lg shadow-card p-8 border border-border">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={32} color="var(--color-success)" />
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground text-center mb-4">
            {t?.successTitle}
          </h2>

          <p className="text-sm text-muted-foreground text-center mb-4">
            {t?.successMessage}
          </p>

          <div className="bg-muted rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                {t?.successNote}
              </p>
            </div>
          </div>

          <Button
            variant="default"
            fullWidth
            onClick={handleBackToLogin}
            iconName="ArrowLeft"
            iconPosition="left"
          >
            {t?.backToLogin}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card rounded-lg shadow-card p-8 border border-border">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Lock" size={32} color="var(--color-primary)" />
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-foreground text-center mb-2">
          {t?.title}
        </h2>

        <p className="text-sm text-muted-foreground text-center mb-8">
          {t?.subtitle}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="email"
            label={t?.emailLabel}
            placeholder={t?.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            error={error}
            required
            disabled={isLoading}
          />

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            iconName="Send"
            iconPosition="right"
          >
            {t?.sendButton}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-2 transition-colors duration-200"
            >
              <Icon name="ArrowLeft" size={16} />
              {t?.backToLogin}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;